import useFilePermissions from "~/hooks/useFilePermissions";

/**
 * Determines whether a user has read or write permissions for a specific file or directory.
 *
 * Handles various permission scenarios:
 * - Wildcard permissions (paths ending with "/*")
 * - Exact path matches
 * - Parent directory permissions (read access to parent paths)
 *
 * @param {string} key - The file or directory path to check permissions for
 * @param {string} permission - The permission type to check ("read" or "write")
 * @returns {boolean|null} True if permitted, false if denied, null if loading
 *
 * @example
 * const canWrite = useFileAccess("raw/my-folder", "write");
 */
const useFileAccess = (key = "", permission: "read" | "write") => {
  const { data: permissions = {}, isLoading } = useFilePermissions();

  if (isLoading) {
    return null;
  }

  return Object.entries(permissions).some(([path, value]) => {
    const hasAccess = value.includes(permission);

    if (hasAccess) {
      if (path.endsWith("/*") && key.startsWith(path.slice(0, -2))) {
        // Key is nested inside wildcard entry access
        return true;
      } else if (path === key) {
        // Exact match
        return true;
      } else if (path.startsWith(key)) {
        // User needs read for levels above their nested file in order to reach it
        // Show read only for paths leading up to nested directory access
        return permission === "read";
      }
    }

    return false;
  });
};

export default useFileAccess;
